import { CampaignType } from './campaignType';

export interface ProductId {
  retailerSku: string;
  retailerId: number;
  stacklineSku?: string;
}

export interface ProductIdWithStacklineSku extends ProductId {
  retailerSku: string;
  retailerId: number;
  stacklineSku: string;
}

export interface RepeatPurchaseCashBackReward {
  brandIds: number[];
  rewardAmountByProduct: { productId: ProductId; amount?: number; percentage?: number; productTitle: string }[];
  expiresInDays: number;
}

export enum OrderPurchaseType {
  STORE = 'STORE',
  ONLINE = 'ONLINE',
}

export type RewardAmountType = 'fixed' | 'percent';

export interface LimitedTimePurchaseReward {
  qualifyingOrderPurchaseType?: OrderPurchaseType;
  maxRewardCountPerCustomer: number;
  rewardAmountByProduct: {
    productId: ProductId;
    amount?: number;
    percent?: number;
    rewardAmountType?: RewardAmountType;
    rewardAmountMax?: number;
  }[];
  expiresInDays: number;
}

export interface AffiliateLinkReward {
  brandIds: number[];
  rewardAmountByProduct: { productId: ProductId; amount: number; productTitle: string }[];
  expiresInDays: number;
}

export interface ReviewCashBackReward {
  rewardAmountByProduct: { productId: ProductId; amount: number }[];
  expiresInDays: number;
}

export interface RewardProcessingFee {
  rewardProcessingFeeByProduct: {
    productId: ProductId;
    amount: number;
    percent?: number;
  }[];
}

export interface PromoteBrandClubCashBackReward {
  rewardAmountForReferrer: number;
  rewardAmountForJoiningUser: number;
  expiresInDays: number;
}

export interface ReferralCashBackReward {
  rewardAmountForReferrer: number;
  rewardAmountForJoiningUser: number;
}

export interface CampaignTarget {
  campaignTargetType: 'brand' | 'category' | 'subCategory' | 'product' | 'keyword';
  targets: {
    targetId: string;
    targetName: string;
  }[];
}

export interface BaseCreateCampaignRequest {
  campaignName: string;
  startDate: Date;
  endDate: Date;
  campaignType:
    | 'reviewRewardCampaign'
    | 'limitedTimePurchaseRewardCampaign'
    | 'purchaseRewardCampaign'
    | 'referralRewardCampaign'
    | CampaignType.promoteBrandClubRewardCampaign
    | 'affiliateLinkCampaign'
    | 'socialRewardCampaign'
    | 'surveyRewardCampaign'
    | 'retailerConnectRewardCampaign'
    | 'pageScanRewardCampaign';
  marketplace: 'US';
  budgetCurrency: 'USD';
  budgetAmountTotal: number;
}

export interface CreateRepeatPurchaseCampaignRequest extends BaseCreateCampaignRequest {
  extendedAttributes: {
    productIds: ProductId[];
    reward: RepeatPurchaseCashBackReward;
  };
}

export interface AssetByProduct {
  productId: ProductId;
}

export interface CreateLimitedTimePurchaseCampaignRequest extends BaseCreateCampaignRequest {
  extendedAttributes: {
    productIds: ProductId[];
    reward: LimitedTimePurchaseReward;
    assets: { assetsByProduct: AssetByProduct[] };
    dealType: 'DEAL_OF_THE_DAY' | 'LIMITED_QUANTITY_DEAL';
  };
}

export interface CreateAffiliateLinkCampaignRequest extends BaseCreateCampaignRequest {
  extendedAttributes: {
    productIds: ProductId[];
    reward: AffiliateLinkReward;
  };
}

export interface CreateReviewRewardCampaignRequest extends BaseCreateCampaignRequest {
  extendedAttributes: {
    productIds: ProductId[];
    reward: ReviewCashBackReward;
    fee?: RewardProcessingFee;
  };
}

export interface CreatePromoteBrandClubRewardCampaign extends BaseCreateCampaignRequest {
  extendedAttributes: {
    productIds: [];
    brandclubIds: string[];
    reward: PromoteBrandClubCashBackReward;
    target?: CampaignTarget;
  };
}

export interface CreateReferralRewardCampaign extends BaseCreateCampaignRequest {
  extendedAttributes: {
    reward: ReferralCashBackReward;
  };
}

export interface SocialReward {
  amount: number;
  percent?: number;
}

export interface Content {
  imageUrl?: string;
  textToShare: string;
}

export interface CreateSocialRewardCampaign extends BaseCreateCampaignRequest {
  extendedAttributes: {
    productIds: ProductId[];
    rewardByPlatform: Record<string, SocialReward>;
    contentByPlatform: Record<string, Content>;
    reward: {
      rewardAmountByProduct: { productId: ProductId; amount: number }[];
      expiresInDays: number;
    };
    contentInvitationTitle: string;
  };
}

export interface CreatePageScanRewardCampaign extends BaseCreateCampaignRequest {
  extendedAttributes: {
    reward: {
      rewardByPlatform: Record<string, SocialReward>;
      expiresInDays: number;
    };
  };
}

export interface CreateSurveyRewardCampaign extends BaseCreateCampaignRequest {
  extendedAttributes: {
    surveyId: string;
    target: {
      productIds?: ProductId[];
      brandclubIds?: string[];
    };
    reward: {
      reward: {
        amount: number;
      };
      expiresInDays: number;
    };
  };
}

export interface CreateRetailerConnectRewardCampaign extends BaseCreateCampaignRequest {
  extendedAttributes: {
    reward: {
      rewardByPlatform: Record<string, SocialReward>;
      expiresInDays: number;
    };
  };
}

/**
 * Targeting type values
 */
export enum CampaignTargetingType {
  /**
   * (A) General Population: This targeting type will reach all relevant customers who engage with your brand across the network.
   * (Note these will be surfaced to non-logged in users on web app. )
   */
  general = 'general',
  /**
   * (B) Brand Buyers: This targeting type will reach all customers who have made a purchase from your brand during the past two years.
   */
  brandBuyers = 'brandBuyers',
  /**
   * brandclub customers matching the list of audience filter
   */
  custom = 'custom',
  audience = 'audience',
}
